<template>
    <BaseForm>
        <template #fields>
            <UserReference
                v-model:query="query"
                :label="$t('responsible')"
                mode="Multiple"
                :assigned-users="assignedUsers"
                :assigned-count="matchingAssignedUsers.length"
                :unassigned-count="matchingUnassignedUsers.length"
            >
                <template #assigned>
                    <UserReferenceUserOption
                        v-for="user in matchingAssignedUsers"
                        :key="user.id"
                        :user="user"
                        is-assigned
                        @select="removeUser(user)"
                    />
                </template>
                <template #unassigned>
                    <UserReferenceUserOption
                        v-for="user in matchingUnassignedUsers"
                        :key="user.id"
                        :user="user"
                        @select="assignUser(user)"
                    />
                </template>
            </UserReference>
            <TextAreaField
                :value="values.responsibleUserNotes"
                :label="$t('responsible-user-notes')"
                :error-messages="errors.responsibleUserNotes"
                :max-length="EventTeamStructureSchema.shape.responsibleUserNotes.maxLength ?? undefined"
                @input="setFieldValue('responsibleUserNotes', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('responsibleUserNotes', true)"
            />
            <TextField
                :value="values.assistant"
                :label="$t('assistant')"
                :error-messages="errors.assistant"
                @input="setFieldValue('assistant', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('assistant', true)"
            />
            <TextAreaField
                :value="values.teamChanges"
                :label="$t('team-changes')"
                :error-messages="errors.teamChanges"
                :max-length="EventTeamStructureSchema.shape.teamChanges.maxLength ?? undefined"
                @input="setFieldValue('teamChanges', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('teamChanges', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import type { UserReferenceUser } from '@/components/UserReference/UserReference.types';
import UserReference from '@/components/UserReference/UserReference.vue';
import UserReferenceUserOption from '@/components/UserReference/UserReferenceUserOption.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useEventResponsibleUsers } from '@/event/composables/useEventResponsibleUsers';
import { useForm } from '@/forms/composables/useForm';
import { compareUsersByName } from '@/user/compareUsersByName';
import { matchesQuery } from '@/utils/splitByMatches';
import { computed, ref } from 'vue';
import { EventTeamStructureSchema } from '../schemas';

const props = defineProps<{ eventId: string }>();
const eventId = computed(() => props.eventId);
const { event, updateEvent } = useEventInjection();
const { savedResponsibleUsers, activeUsers, usersById } = useEventResponsibleUsers();

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    EventTeamStructureSchema,
    computed(() => ({
        responsibleUserIds: savedResponsibleUsers.value.map((user) => user.id),
        responsibleUserNotes: event.value?.responsibleUserNotes ?? '',
        assistant: event.value?.assistant ?? '',
        teamChanges: event.value?.teamChanges ?? '',
    })),
);

function matchesUser(user: UserReferenceUser) {
    return matchesQuery(`${user.firstName} ${user.lastName}`, query.value);
}

const query = ref('');

const assignedUsers = computed(() =>
    values.value.responsibleUserIds.flatMap((id) => usersById.value.get(id) ?? []).toSorted(compareUsersByName),
);

const matchingAssignedUsers = computed(() => assignedUsers.value.filter(matchesUser));

const matchingUnassignedUsers = computed(() =>
    activeUsers.value
        .filter((user) => !values.value.responsibleUserIds.includes(user.id) && matchesUser(user))
        .toSorted(compareUsersByName),
);

function assignUser(user: UserReferenceUser) {
    setFieldValue('responsibleUserIds', [...values.value.responsibleUserIds, user.id]);
}

function removeUser(user: UserReferenceUser) {
    setFieldValue(
        'responsibleUserIds',
        values.value.responsibleUserIds.filter((id) => id !== user.id),
    );
}

async function save() {
    const valid = await validate();
    if (valid) {
        const { responsibleUserIds, ...fields } = values.value;
        const savedIds = savedResponsibleUsers.value.map((user) => user.id);
        await updateEvent({
            id: eventId.value,
            ...fields,
            createResponsibleUsers: responsibleUserIds
                .filter((id) => !savedIds.includes(id))
                .map((userId) => ({ userId })),
            deleteResponsibleUsers: savedIds
                .filter((id) => !responsibleUserIds.includes(id))
                .map((userId) => ({ eventId: eventId.value, userId })),
        });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
